import { MovableDialog } from '@/components/shared/MovableDialog';
import { type ReactElement } from 'react';
import type { SpecFile } from '@/api/v2Client';
import { formatDate } from '@/lib/format';

/**
 * Phase 5.X-v1-parity-polish Feature 2 — SpecFile summary modal.
 *
 * V1 had a dedicated "View Summary" modal on its SpecFilesScreen showing
 * filename, version, sections list, SHA-256, and uploaded-by/at. V2's
 * SpecFile row already carries all the metadata V1's modal needed (the
 * row response is the full table row), so this modal renders directly
 * off the row passed in — no extra round-trip.
 *
 * If a future phase wants to surface parsed DTD/XSD element counts or
 * embedded XSL targetNamespace inspection, that's an enhancement to this
 * modal + a new backend `/summary` route. Per this phase's "Out of scope"
 * note, ML-based extraction stays out — only what the row already has.
 *
 * (formatSha256Short lives in its own file — see ./formatSha256Short.ts —
 * to satisfy the react-refresh/only-export-components lint rule.)
 */

/**
 * Format a FileSize value (bigint serialized as number or string) for
 * human display.
 */
function formatFileSize(size: number | string | null | undefined): string {
  if (size == null || size === '') return '—';
  const n = typeof size === 'string' ? Number(size) : size;
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export interface SpecFileSummaryModalProps {
  readonly entry: SpecFile;
  readonly onClose: () => void;
}

export function SpecFileSummaryModal({
  entry,
  onClose,
}: SpecFileSummaryModalProps): ReactElement {
  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="specFiles.summary"
      title={
        <span id="spec-file-summary-title">
          {entry.Title}
          <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>
            {entry.Region} · {entry.FileType}
            {entry.Module ? ` · Module ${entry.Module}` : ''}
            {entry.Version ? ` · v${entry.Version}` : ''}
          </span>
        </span>
      }
      ariaLabelledBy="spec-file-summary-title"
      testId="spec-file-summary-modal"
      onClose={onClose}
      closeOnBackdrop
      width="min(560px, 90vw)"
      maxHeight="80vh"
      zIndex={1000}
    >
      <>
        <div style={{ padding: 20, overflowY: 'auto', flex: 1, fontSize: 13 }}>
          <SummaryRow label="Region" value={entry.Region} />
          <SummaryRow label="File type" value={entry.FileType} mono />
          <SummaryRow label="Module" value={entry.Module ?? '—'} />
          <SummaryRow label="Version" value={entry.Version ?? '—'} mono />
          <SummaryRow
            label="Active"
            value={entry.IsActive ? '✓ Yes' : '✗ No'}
          />
          {entry.Description && (
            <SummaryRow label="Description" value={entry.Description} block />
          )}
          <SummaryRow label="File name" value={entry.FileName ?? '—'} mono />
          <SummaryRow label="File size" value={formatFileSize(entry.FileSize)} />
          <SummaryRow label="MIME type" value={entry.MimeType ?? '—'} mono />
          <SummaryRow
            label="SHA-256"
            value={entry.Sha256 ?? '—'}
            mono
            block
            testId="spec-file-summary-sha256"
          />
          <SummaryRow label="Storage path" value={entry.StoragePath ?? '—'} mono block />
          <SummaryRow
            label="Created"
            value={`${formatDate(entry.CreatedAt)} by ${entry.CreatedBy}`}
          />
          {entry.UpdatedAt && (
            <SummaryRow
              label="Updated"
              value={`${formatDate(entry.UpdatedAt)}${entry.UpdatedBy ? ` by ${entry.UpdatedBy}` : ''}`}
            />
          )}
          <SummaryRow label="Tenant" value={entry.TenantId ?? 'Global'} mono />
        </div>

        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="spec-file-summary-close"
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '6px 16px',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Close
          </button>
        </div>
      </>
    </MovableDialog>
  );
}

interface SummaryRowProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly block?: boolean;
  readonly testId?: string;
}

function SummaryRow({ label, value, mono, block, testId }: SummaryRowProps): ReactElement {
  return (
    <div
      style={{
        display: block ? 'block' : 'flex',
        justifyContent: 'space-between',
        gap: 12,
        padding: '6px 0',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ fontWeight: 600, color: 'var(--text-muted)', minWidth: 110 }}>
        {label}
      </div>
      <div
        data-testid={testId}
        style={{
          textAlign: block ? 'left' : 'right',
          fontFamily: mono ? 'monospace' : undefined,
          color: 'var(--text)',
          wordBreak: 'break-all',
          fontSize: mono ? 12 : 13,
          marginTop: block ? 4 : 0,
        }}
      >
        {value}
      </div>
    </div>
  );
}
